/**
 * Next.js CLIENT instrumentation hook (issue #10679, part of #8125).
 *
 * Next.js executes this file's top-level code in the browser before the app
 * hydrates — it is the earliest client-side hook the framework offers
 * (supported since 15.3; this repo is on `next@^16.2.9`). There is nothing to
 * export: the module IS the side effect.
 *
 * ## Why: zod 4's JIT validator codegen is the last CSP `script-src` violator
 *
 * Production `csp_violation.logged` events (after #12331 added
 * `source-file` / `line-number` / `column-number`) pinned every remaining
 * `blocked-uri="eval"` report to one vendored chunk, at two columns, both
 * inside zod 4.3.6:
 *
 *   - `zod/v4/core/util.js` — `allowsEval`, a `new Function('')` capability
 *     probe.
 *   - `zod/v4/core/schemas.js` + `Doc.compile()` — the per-object "fastpass"
 *     validator, generated with `new Function(...)`.
 *
 * Both are `eval`-class operations, so an enforcing
 * `Content-Security-Policy` without `'unsafe-eval'` blocks them and (in
 * report-only mode) files a violation report per runtime, on every route that
 * parses with zod client-side. Turning the JIT off removes both at the source,
 * which is what lets `script-src` keep `'strict-dynamic'` and stay free of
 * `'unsafe-eval'`.
 *
 * Zod's own fallback would have kept PARSING working under enforce mode
 * regardless — `new Function('')` throws, `allowsEval` resolves to `false`,
 * and the interpreted path takes over. What that fallback does not avoid is
 * the report noise, because the probe still runs and still gets blocked. This
 * hook avoids the probe entirely: `fastEnabled = jit && allowsEval.value`
 * short-circuits, so a jitless runtime never reads it.
 *
 * ## Why HERE and not in an app module
 *
 * Zod reads `globalConfig.jitless` when a schema is CONSTRUCTED, not when it
 * parses, and captures the answer per schema. Setting the config from any
 * ordinary module makes the ordering merely likely; setting it from the
 * framework's designated client-bootstrap file makes it guaranteed, because
 * Next runs this before any application code.
 *
 * ## Why NOT in `src/instrumentation.ts`
 *
 * `globalConfig` is per-runtime, and this hook is the client half only — the
 * server keeps its compiled validators. That is deliberate: the server
 * executes no browser policy and emits no CSP reports, so disabling its JIT
 * would be a pure throughput cost for zero CSP benefit. `tests/unit/
 * instrumentation-client.test.ts` guards that the config does not migrate
 * into the server hook.
 *
 * Rollback for the enforce flip itself is an env var, not a revert: unset
 * `CSP_MODE` (or set it to `report-only`) in Railway. This file is safe to
 * keep in either mode.
 */

import { z } from 'zod'

// Imports are evaluated before `z.config` below runs, so nothing in this
// module's import graph may construct a zod schema (see "Why HERE" above).
import { installChunkLoadRecovery } from '@/lib/chunk-load-recovery'

z.config({ jitless: true })

/**
 * ## Chunk-load recovery (issue #14964, part of #14861)
 *
 * During a rolling update a page rendered by one build can request a chunk
 * from a pod running the other and get a 404. Registering here — before
 * hydration, before the first lazy chunk is requested — means a resulting
 * `ChunkLoadError` triggers one hard reload per deployment (#15676) instead
 * of leaving a broken page. The logic and its loop guard live in
 * `src/lib/chunk-load-recovery.ts`. The `window` check keeps this module
 * importable outside a browser (its unit tests run in node).
 */
if (typeof window !== 'undefined') {
  installChunkLoadRecovery(window)
}
